import {
	Message as AeMessage,
	MessageAction,
	MessageActions,
	MessageContent,
	MessageResponse,
} from "@/components/ai-elements/message";
import {
	Reasoning,
	ReasoningContent,
	ReasoningTrigger,
} from "@/components/ai-elements/reasoning";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
	CheckIcon,
	CopyIcon,
	PencilIcon,
	RefreshCwIcon,
} from "lucide-react";
import { useState } from "react";
import { MentionedText } from "./MentionChip.js";
import { cn } from "./cn.js";
import { CloneSiteCards } from "./CloneSiteCard.js";
import { DesignSiteCards } from "./DesignSiteCard.js";
import { CompanyPackCards } from "./CompanyPackCard.js";
import { ConnectCards } from "./ConnectCards.js";
import { HandoffCards } from "./HandoffCards.js";
import {
	hideAssistantBodyForCompanyResearch,
	hideAssistantBodyForSiteClone,
	isStatusFillerBody,
	type CardAction,
	type ChatCompanyResearch,
	type ChatHandoff,
	type ChatMessage,
	type ChatSiteDesign,
	type ChatWorkingCopyAsk,
	type HandoffDecisionEvent,
	type PublishDecisionEvent,
	type SiteCloneCardEvent,
} from "./messages.js";
import { WorkingCopyAskCard } from "./WorkingCopyAsk.js";
import type { AgentId } from "./nav.js";

function formatTime(iso: string): string {
	try {
		const then = new Date(iso).getTime();
		const diffMs = Date.now() - then;
		const mins = Math.floor(diffMs / 60_000);
		if (mins < 1) return "Just now";
		if (mins < 60) return `${mins}m ago`;
		const hours = Math.floor(mins / 60);
		if (hours < 24) return `${hours}h ago`;
		const days = Math.floor(hours / 24);
		if (days < 14) return `${days}d ago`;
		return new Intl.DateTimeFormat(undefined, {
			month: "short",
			day: "numeric",
		}).format(new Date(iso));
	} catch {
		return "";
	}
}

function initials(name: string): string {
	return name
		.split(/\s+/)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? "")
		.join("");
}

export type { HandoffDecisionEvent } from "./messages.js";

type Props = {
	message: ChatMessage;
	/** `chat` = Claude-style (no names/avatars). `desk` keeps specialist chrome. */
	variant?: "chat" | "desk";
	busy?: boolean;
	onCardAction?: (action: CardAction) => void;
	onEdit?: (messageId: string, body: string) => void;
	onRetry?: (messageId: string) => void;
	onOpenAgent?: (id: AgentId) => void;
	onOpenSitePreview?: (url: string) => void;
	onHandoffPatch?: (handoffId: string, patch: Partial<ChatHandoff>) => void;
	/** After a successful in-chat gate decision — patch + start a reassurance turn. */
	onHandoffDecision?: (event: HandoffDecisionEvent) => void;
	/** Ask the agent to publish a preview-ready site (opens a gated approval). */
	onPublishSite?: (siteId: string, siteName: string) => void;
	onPublishDecision?: (event: PublishDecisionEvent) => void;
	onSiteCloneCard?: (event: SiteCloneCardEvent) => void;
	onCompanyResearchPatch?: (
		packId: string,
		patch: Partial<ChatCompanyResearch>,
	) => void;
	onConfirmCompanyPack?: (packId: string) => void;
	onSiteDesignPatch?: (siteId: string, patch: Partial<ChatSiteDesign>) => void;
	confirmingPackId?: string | null;
	onWorkingCopyYes?: (ask: ChatWorkingCopyAsk, messageId: string) => void;
	onWorkingCopyLater?: (messageId: string) => void;
	workingCopyPaying?: boolean;
	onWorkingCopyPaid?: () => void;
};

/** Chat row — AI Elements Message + Reasoning + Actions. */
export function Message({
	message,
	variant = "desk",
	busy = false,
	onCardAction,
	onEdit,
	onRetry,
	onOpenAgent,
	onOpenSitePreview,
	onHandoffPatch,
	onHandoffDecision,
	onPublishSite,
	onPublishDecision,
	onSiteCloneCard,
	onCompanyResearchPatch,
	onConfirmCompanyPack,
	onSiteDesignPatch,
	confirmingPackId,
	onWorkingCopyYes,
	onWorkingCopyLater,
	workingCopyPaying = false,
	onWorkingCopyPaid,
}: Props) {
	const from = message.author === "human" ? "user" : "assistant";
	const minimal = variant === "chat";
	const [copied, setCopied] = useState(false);
	const [editing, setEditing] = useState(false);
	const [editDraft, setEditDraft] = useState(message.body);

	const copyBody = async () => {
		if (!message.body) return;
		try {
			await navigator.clipboard.writeText(message.body);
			setCopied(true);
			window.setTimeout(() => setCopied(false), 1600);
		} catch {
			/* ignore */
		}
	};

	const hideBody =
		from === "assistant" &&
		(hideAssistantBodyForCompanyResearch(
			message.companyResearch,
			message.body,
		) ||
			hideAssistantBodyForSiteClone(message.siteClones) ||
			(isStatusFillerBody(message.body) && !message.workingCopyAsk));

	const showActions =
		Boolean(message.body) &&
		!hideBody &&
		!message.pending &&
		!editing &&
		(from === "user" || from === "assistant");

	const canEdit = from === "user" && Boolean(onEdit) && !busy;
	const canRetry = Boolean(onRetry) && !busy && !message.pending;

	const commitEdit = () => {
		const next = editDraft.trim();
		setEditing(false);
		if (!next || next === message.body.trim() || !onEdit) return;
		onEdit(message.id, next);
	};

	return (
		<div
			className={
				from === "user"
					? cn(
							"flex items-start justify-end gap-2.5",
							editing && "w-full",
						)
					: "flex items-start gap-2.5"
			}
		>
			{from === "assistant" && !minimal && (
				<div className="avatar avatar-agent" aria-hidden>
					{message.avatar ? (
						<img src={message.avatar} alt="" />
					) : (
						initials(message.authorName) || "·"
					)}
				</div>
			)}
			<div
				className={
					from === "user"
						? cn(
								"flex min-w-0 max-w-[min(100%,36rem)] flex-col items-end",
								editing && "w-full",
							)
						: "min-w-0 flex-1"
				}
			>
				{!minimal && from === "assistant" ? (
					<div className="msg-head">
						<span className="msg-author">{message.authorName}</span>
						<time className="msg-time" dateTime={message.at}>
							{formatTime(message.at)}
						</time>
					</div>
				) : null}

				<AeMessage
					from={from}
					className={cn("max-w-full", editing && "w-full")}
				>
					{from === "assistant" &&
					message.connects &&
					message.connects.length > 0 ? (
						<ConnectCards connects={message.connects} />
					) : null}
					{from === "assistant" &&
					message.siteClones &&
					message.siteClones.length > 0 ? (
						<CloneSiteCards
							clones={message.siteClones}
							onOpenSitePreview={onOpenSitePreview}
							onPublish={onPublishSite}
							onPublishDecision={onPublishDecision}
							onCardEvent={onSiteCloneCard}
						/>
					) : null}
					{from === "assistant" &&
					message.siteDesigns &&
					message.siteDesigns.length > 0 ? (
						<DesignSiteCards
							rows={message.siteDesigns}
							onOpenSitePreview={onOpenSitePreview}
							onPatch={onSiteDesignPatch}
						/>
					) : null}
					{from === "assistant" &&
					message.companyResearch &&
					message.companyResearch.length > 0 ? (
						<CompanyPackCards
							rows={message.companyResearch}
							onPatch={onCompanyResearchPatch}
							onConfirm={onConfirmCompanyPack}
							confirmingId={confirmingPackId}
						/>
					) : null}
					{from === "assistant" && message.reasoning?.trim() ? (
						<Reasoning
							className="mb-2 w-full"
							isStreaming={Boolean(
								message.pending && !message.body.trim(),
							)}
						>
							<ReasoningTrigger />
							<ReasoningContent>{message.reasoning}</ReasoningContent>
						</Reasoning>
					) : null}

					{/* Work results first — then the reply. Gate CTA stays last. */}
					{from === "assistant" &&
					!editing &&
					message.handoffs &&
					message.handoffs.length > 0 ? (
						<HandoffCards
							slot="work"
							handoffs={message.handoffs}
							onOpenAgent={onOpenAgent}
							onOpenSitePreview={onOpenSitePreview}
							onHandoffPatch={onHandoffPatch}
							onHandoffDecision={onHandoffDecision}
						/>
					) : null}

					{editing ? (
						<div className="msg-edit w-full">
							<Textarea
								value={editDraft}
								onChange={(event) => setEditDraft(event.target.value)}
								className="min-h-28 w-full resize-y bg-mx-elevated text-[13px]"
								autoFocus
								onKeyDown={(event) => {
									if (event.key === "Escape") {
										event.preventDefault();
										setEditing(false);
										setEditDraft(message.body);
									}
									if (
										(event.metaKey || event.ctrlKey) &&
										event.key === "Enter"
									) {
										event.preventDefault();
										commitEdit();
									}
								}}
							/>
							<div className="mt-2 flex justify-end gap-2">
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => {
										setEditing(false);
										setEditDraft(message.body);
									}}
								>
									Cancel
								</Button>
								<Button
									type="button"
									size="sm"
									disabled={!editDraft.trim() || busy}
									onClick={commitEdit}
								>
									Save & retry
								</Button>
							</div>
						</div>
					) : (
						<MessageContent
							className={cn(
								from === "assistant" && "w-full max-w-none",
								workingCopyPaying && "overflow-visible",
							)}
						>
							{message.attachments && message.attachments.length > 0 ? (
								<div className="msg-attachments mb-2">
									{message.attachments.map((att) =>
										att.mediaType.startsWith("image/") && att.url ? (
											<img
												key={att.id}
												className="msg-attach-img"
												src={att.url}
												alt={att.filename ?? "Attachment"}
											/>
										) : (
											<div key={att.id} className="msg-attach-file">
												{att.filename ?? att.mediaType}
											</div>
										),
									)}
								</div>
							) : null}
							{message.body && !hideBody ? (
								from === "assistant" ? (
									<MessageResponse isAnimating={Boolean(message.pending)}>
										{message.body}
									</MessageResponse>
								) : (
									<MentionedText text={message.body} />
								)
							) : null}
							{from === "assistant" &&
							message.workingCopyAsk &&
							onWorkingCopyYes ? (
								<WorkingCopyAskCard
									ask={message.workingCopyAsk}
									paying={workingCopyPaying}
									onYes={() =>
										onWorkingCopyYes(message.workingCopyAsk!, message.id)
									}
									onLater={() => onWorkingCopyLater?.(message.id)}
									onPaid={onWorkingCopyPaid}
								/>
							) : null}
							{message.card ? (
								<div className="msg-card mt-2.5">
									<h3>{message.card.title}</h3>
									<p className="msg-card-body">{message.card.body}</p>
									{(message.card.primaryLabel ||
										message.card.secondaryLabel) && (
										<div className="msg-card-actions">
											{message.card.primaryLabel && (
												<Button
													type="button"
													disabled={
														!message.card.primaryAction || !onCardAction
													}
													onClick={() => {
														if (
															message.card?.primaryAction &&
															onCardAction
														) {
															onCardAction(message.card.primaryAction);
														}
													}}
												>
													{message.card.primaryLabel}
												</Button>
											)}
											{message.card.secondaryLabel && (
												<Button
													type="button"
													variant="outline"
													disabled={
														!message.card.secondaryAction || !onCardAction
													}
													onClick={() => {
														if (
															message.card?.secondaryAction &&
															onCardAction
														) {
															onCardAction(message.card.secondaryAction);
														}
													}}
												>
													{message.card.secondaryLabel}
												</Button>
											)}
										</div>
									)}
								</div>
							) : null}
						</MessageContent>
					)}

					{/* Pending Approve/Reject only — final CTA under the prose. */}
					{from === "assistant" &&
					!editing &&
					message.handoffs &&
					message.handoffs.length > 0 ? (
						<HandoffCards
							slot="gate"
							handoffs={message.handoffs}
							onOpenAgent={onOpenAgent}
							onOpenSitePreview={onOpenSitePreview}
							onHandoffPatch={onHandoffPatch}
							onHandoffDecision={onHandoffDecision}
						/>
					) : null}

					{showActions ? (
						<MessageActions
							className={
								from === "user"
									? "mt-1 justify-end opacity-70 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 max-sm:opacity-100"
									: "mt-1 opacity-70 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 max-sm:opacity-100"
							}
						>
							{minimal ? (
								<span className="mr-1 text-[11px] text-mx-quaternary tabular-nums">
									{formatTime(message.at)}
								</span>
							) : null}
							{canRetry ? (
								<MessageAction
									tooltip="Retry"
									label="Retry"
									disabled={busy}
									onClick={() => onRetry?.(message.id)}
								>
									<RefreshCwIcon className="size-3.5" />
								</MessageAction>
							) : null}
							{canEdit ? (
								<MessageAction
									tooltip="Edit"
									label="Edit"
									disabled={busy}
									onClick={() => {
										setEditDraft(message.body);
										setEditing(true);
									}}
								>
									<PencilIcon className="size-3.5" />
								</MessageAction>
							) : null}
							<MessageAction
								tooltip={copied ? "Copied" : "Copy"}
								label="Copy"
								onClick={() => void copyBody()}
							>
								{copied ? (
									<CheckIcon className="size-3.5" />
								) : (
									<CopyIcon className="size-3.5" />
								)}
							</MessageAction>
						</MessageActions>
					) : null}
				</AeMessage>
			</div>
		</div>
	);
}
